import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, IconButton, Chip, useTheme } from '@mui/material';
import { Pencil, Trash2, Globe, GripVertical } from 'lucide-react';
import { AIFieldNodeData } from '../../types/form';

const HTTP_COLOR = '#EA580C';

const METHOD_COLORS: Record<string, string> = {
  GET: '#16A34A',
  POST: '#2563EB',
  PUT: '#D97706',
  DELETE: '#DC2626',
  PATCH: '#7C3AED',
};

const HttpRequestFieldNode: React.FC<NodeProps<AIFieldNodeData>> = ({ data }) => {
  const theme = useTheme();
  const { field, onEdit, onDelete } = data;
  const config = field.httpRequestConfig;
  const method = config?.method || 'GET';
  const url = config?.url || '';

  // Truncate URL for display
  const truncatedUrl = url.length > 50 ? url.substring(0, 50) + '...' : url;

  // Extract referenced field IDs from URL and body
  const allText = `${config?.url || ''} ${config?.body || ''} ${(config?.headers || []).map(h => `${h.key} ${h.value}`).join(' ')}`;
  const referencedIds = [...new Set(allText.match(/\{(\w+)\}/g)?.map(m => m.slice(1, -1)) || [])];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `2px solid ${HTTP_COLOR}`,
        borderRadius: 2,
        minWidth: 300,
        maxWidth: 400,
        boxShadow: theme.shadows[1],
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: HTTP_COLOR,
          width: 8,
          height: 8,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${HTTP_COLOR}30`,
          backgroundColor: `${HTTP_COLOR}10`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GripVertical size={14} color={theme.palette.text.secondary} style={{ cursor: 'grab' }} />
          <Globe size={14} color={HTTP_COLOR} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: HTTP_COLOR }}>
            HTTP Request
          </Typography>
          <Chip
            label={method}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              backgroundColor: `${METHOD_COLORS[method] || HTTP_COLOR}20`,
              color: METHOD_COLORS[method] || HTTP_COLOR,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            #{field.order}
          </Typography>
        </Box>
        <Box>
          <IconButton size="small" onClick={() => onEdit(field.fieldId)} sx={{ mr: 0.5 }}>
            <Pencil size={14} />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(field.fieldId)}>
            <Trash2 size={14} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {field.label || 'Untitled HTTP Request'}
        </Typography>

        {truncatedUrl && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mb: 1,
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              lineHeight: 1.4,
              wordBreak: 'break-all',
            }}
          >
            {truncatedUrl}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={field.fieldId}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
          <Chip
            label="Silent"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              backgroundColor: `${HTTP_COLOR}20`,
              color: HTTP_COLOR,
            }}
          />
          {referencedIds.map(refId => (
            <Chip
              key={refId}
              label={`{${refId}}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                backgroundColor: `${HTTP_COLOR}15`,
                color: HTTP_COLOR,
                borderColor: `${HTTP_COLOR}40`,
              }}
              variant="outlined"
            />
          ))}
        </Box>
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: HTTP_COLOR,
          width: 8,
          height: 8,
        }}
      />
    </Box>
  );
};

export default memo(HttpRequestFieldNode);
